import { Checkbox, ClassName } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import DraggableList, { IUpdatedDraggable } from 'components/foundation/DraggableList/draggable-list/DraggableList';
import useSafetyDashboardSettingsTitleLookup from 'containers/safety-overview/hooks/useSafetyDashboardSettingsTitleLookup/useSafetyDashboardSettingsTitleLookup';
import { TSafetyDashboardGridSettingKey } from 'containers/safety-overview/types/types';
import { cloneDeep } from 'lodash';
import { useMemo } from 'react';
import { TSafetySummaryDashboardListSettingsItem } from '../types/types';

export type TSafetySummaryDraggableSetting = {
  id: TSafetyDashboardGridSettingKey;
  title: string;
  visible: boolean;
  draggable: true;
  position: number;
};

export interface ISafetySummaryDashboardListSettingsProps {
  items: TSafetySummaryDashboardListSettingsItem[];
  onItemsUpdate: (items: TSafetySummaryDashboardListSettingsItem[]) => void;
  className?: ClassName;
}

export default function SafetySummaryDashboardListSettings({
  items,
  onItemsUpdate,
  className,
}: ISafetySummaryDashboardListSettingsProps) {
  const titleLookup = useSafetyDashboardSettingsTitleLookup();

  const listItems: TSafetySummaryDraggableSetting[] = useMemo(() => {
    return items.map((item, index) => ({
      ...item,
      title: titleLookup[item.id],
      position: index,
      draggable: true,
    }));
  }, [items, titleLookup]);

  function onCheckChangeCurried(index: number) {
    return (visible: boolean) => {
      const itemsCopy = cloneDeep(items);
      itemsCopy[index] = { ...itemsCopy[index], visible };
      onItemsUpdate(itemsCopy);
    };
  }

  function onUpdate(items: Array<IUpdatedDraggable<TSafetySummaryDraggableSetting>>) {
    onItemsUpdate(items.map(({ payload }) => ({ id: payload.id, visible: payload.visible })));
  }

  const classes = classNames('safety-summary-dashboard-list-settings', className);

  return (
    <DraggableList
      hover={true}
      itemClass="py-6"
      onClick={(item, index) => {
        onCheckChangeCurried(index)(!item.visible);
      }}
      renderItem={(item, index) => (
        <Checkbox
          className="safety-summary-dashboard-list-checkbox-item"
          checked={item.visible}
          onChange={onCheckChangeCurried(index)}
          label={item.title}
        />
      )}
      items={listItems}
      onUpdate={onUpdate}
      className={classes}
      dragHandleAlignment="right"
    />
  );
}
